import type { Lang } from '../context/LanguageContext'

/**
 * Copy for the ESCA executive-search landing (/esca).
 * Only UA and EN exist here — the switcher on this page offers those two.
 * A visitor arriving with RU stored from the legacy site falls back to UA.
 */
export type EscaLang = 'UA' | 'EN'

export function toEscaLang(lang: Lang): EscaLang {
  return lang === 'EN' ? 'EN' : 'UA'
}

export interface EscaCopy {
  nav: {
    expertise: string
    research: string
    cases: string
    about: string
    cta: string
    menu: string
    close: string
  }
  hero: {
    eyebrow: string
    h1: string
    lead: string
    cta: string
    facts: Array<{ value: string; label: string }>
    visualCaption: string
  }
  scenarios: {
    title: string
    items: Array<{ title: string; text: string }>
  }
  expertise: {
    title: string
    lead: string
    categories: Array<{ name: string; roles: string[] }>
    footTitle: string
    footText: string
    footCta: string
  }
  research: {
    eyebrow: string
    title: string
    lead: string
    funnel: Array<{ value: string; label: string }>
    steps: Array<{ title: string; text: string }>
  }
  process: {
    title: string
    lead: string
    steps: string[]
    timeValue: string
    timeLabel: string
    timeCaveat: string
  }
  shortlist: {
    title: string
    lead: string
    demoNote: string
    stages: Array<{ value: string; label: string }>
    deliverables: string[]
  }
  cases: {
    title: string
    lead: string
    placeholderNote: string
    roleLabel: string
    industryLabel: string
    geoLabel: string
    challengeLabel: string
    approachLabel: string
    resultLabel: string
    cta: string
    items: Array<{
      role: string
      industry: string
      geo: string
      challenge: string
      approach: string
      result: string
    }>
  }
  founder: {
    title: string
    name: string
    role: string
    paragraphs: string[]
  }
  proof: {
    title: string
    stats: Array<{ value: string; label: string }>
    logosTitle: string
    quotesTitle: string
    quotes: Array<{ quote: string; author: string }>
  }
  geography: {
    title: string
    lead: string
    regions: string[]
  }
  guarantee: {
    title: string
    value: string
    valueLabel: string
    text: string
  }
  price: {
    title: string
    lead: string
    factors: string[]
    note: string
    cta: string
  }
  faq: {
    title: string
    items: Array<{ q: string; a: string }>
  }
  contact: {
    title: string
    lead: string
    nameLabel: string
    namePlaceholder: string
    companyLabel: string
    companyPlaceholder: string
    contactLabel: string
    contactPlaceholder: string
    roleLabel: string
    rolePlaceholder: string
    submit: string
    submitting: string
    disclaimer: string
    successTitle: string
    successText: string
    successReset: string
    errorGeneric: string
    errors: {
      name: string
      contact: string
      contactInvalid: string
      role: string
    }
  }
  footer: {
    tagline: string
    navTitle: string
    contactTitle: string
    legacyTitle: string
    legacyLinks: Array<{ label: string; to: string }>
    rights: string
  }
  stickyCta: string
}

const UA: EscaCopy = {
  nav: {
    expertise: 'Executive Search Expertise',
    research: 'How We Search',
    cases: 'Cases',
    about: 'About ESCA',
    cta: 'Обговорити вакансію',
    menu: 'Меню',
    close: 'Закрити',
  },
  hero: {
    eyebrow: 'Executive Search / Worldwide',
    h1: 'Знаходимо керівників і рідкісних спеціалістів, яких немає у відкритому пошуку',
    lead: 'Executive Search для C-level, B-level і складних IT-позицій. Досліджуємо ринок, виходимо напряму на потрібних людей і приводимо в діалог кандидатів, які самі не шукають нову роботу.',
    cta: 'Обговорити вакансію',
    facts: [
      { value: '20+', label: 'років досвіду' },
      { value: '3 500+', label: 'закритих вакансій' },
      { value: '500+', label: 'компаній-клієнтів' },
      { value: 'Worldwide', label: 'географія пошуку' },
    ],
    visualCaption: 'Market map · демонстрація інтерфейсу',
  },
  scenarios: {
    title: 'Коли вакансії недостатньо, ми йдемо за кандидатом напряму',
    items: [
      {
        title: 'Потрібно посилити керівництво',
        text: 'CEO, COO, CFO, CTO або керівник функції, від якого залежить результат бізнесу.',
      },
      {
        title: 'Пошук зайшов у глухий кут',
        text: 'Внутрішня HR-команда вже пройшла очевидний ринок, але сильних кандидатів недостатньо.',
      },
      {
        title: 'Потрібна конфіденційна заміна',
        text: 'Шукаємо нового керівника без публічної вакансії.',
      },
      {
        title: 'Компанія виходить на новий ринок',
        text: 'Знаходимо перших керівників і ключових спеціалістів в іншій країні.',
      },
    ],
  },
  expertise: {
    title: 'Беремося за позиції, де стандартного підбору вже недостатньо',
    lead: 'Під кожну категорію ми будуємо окрему карту ринку — перелік ролей нижче показує, з чим працюємо найчастіше.',
    categories: [
      {
        name: 'C-level & Executive',
        roles: [
          'CEO / Генеральний директор',
          'COO / Операційний директор',
          'CFO / Фінансовий директор',
          'CTO / Технічний директор',
          'CMO / Директор з маркетингу',
          'CHRO / HR директор',
          'Country Manager',
          'Board Member / Радник',
        ],
      },
      {
        name: 'IT & Digital',
        roles: [
          'VP of Engineering',
          'Head of Engineering',
          'Tech Lead / Team Lead',
          'Solution / Cloud Architect',
          'Senior Backend / Frontend Developer',
          'DevOps / SRE Engineer',
          'Data Scientist / ML Engineer',
          'Product Manager',
        ],
      },
      {
        name: 'Finance & Banking',
        roles: [
          'Finance Director',
          'Head of Treasury',
          'Financial Controller',
          'Head of Internal Audit',
          'Risk Manager',
          'Head of Compliance',
          'Investment Manager',
          'Chief Accountant',
        ],
      },
      {
        name: 'Sales & Business Development',
        roles: [
          'Commercial Director',
          'Head of Sales',
          'Business Development Director',
          'Key Account Director',
          'Head of B2B Sales',
          'Regional Sales Manager',
          'Partnerships Director',
        ],
      },
      {
        name: 'Marketing & PR',
        roles: [
          'Marketing Director',
          'Head of Brand',
          'Head of Performance Marketing',
          'Head of Communications',
          'PR Director',
          'Head of Product Marketing',
          'Creative Director',
        ],
      },
      {
        name: 'HR & People',
        roles: [
          'HR Director',
          'Head of Talent Acquisition',
          'C&B Manager',
          'Head of L&D',
          'HR Business Partner',
          'People Operations Lead',
        ],
      },
      {
        name: 'Logistics & Operations',
        roles: [
          'Operations Director',
          'Supply Chain Director',
          'Head of Logistics',
          'Plant / Production Director',
          'Procurement Director',
          'Head of Warehouse Operations',
        ],
      },
      {
        name: 'Legal',
        roles: [
          'Legal Director',
          'General Counsel',
          'Head of Corporate Law',
          'Compliance Officer',
          'M&A Lawyer',
          'Contract Manager',
        ],
      },
      {
        name: 'E-commerce & Retail',
        roles: [
          'E-commerce Director',
          'Head of Retail',
          'Category Director',
          'Head of Marketplace',
          'Retail Operations Manager',
          'Head of Customer Experience',
        ],
      },
    ],
    footTitle: 'Не знайшли потрібну роль?',
    footText:
      'Під кожну позицію досліджуємо ринок окремо і визначаємо, де знаходяться спеціалісти з потрібним досвідом.',
    footCta: 'Обговорити позицію',
  },
  research: {
    eyebrow: 'How We Search',
    title: 'Ми не чекаємо відгуків. Ми самі знаходимо людей, які потрібні вашому бізнесу',
    lead: 'Кожен пошук починається з дослідження ринку, а не з публікації вакансії.',
    funnel: [
      { value: 'MARKET', label: 'карта ринку' },
      { value: '156', label: 'profiles' },
      { value: '37', label: 'matches' },
      { value: '11', label: 'contacted' },
      { value: '4', label: 'shortlisted' },
    ],
    steps: [
      {
        title: 'Market Mapping',
        text: 'Визначаємо компанії, галузі та ринки, де може знаходитись потрібний спеціаліст.',
      },
      {
        title: 'Research',
        text: 'Знаходимо потенційних кандидатів і збираємо профілі за межами job-бордів.',
      },
      {
        title: 'Direct Hunting',
        text: 'Виходимо напряму на людей, які не перебувають в активному пошуку роботи.',
      },
      {
        title: 'Qualification',
        text: 'Перевіряємо досвід, мотивацію та очікування щодо ролі й компенсації.',
      },
      {
        title: 'Shortlist',
        text: 'Передаємо клієнту відібраних кандидатів разом зі зворотним звʼязком від ринку.',
      },
    ],
  },
  process: {
    title: 'Ви розумієте, що відбувається з пошуком на кожному етапі',
    lead: 'Жоден крок не залишається чорною скринькою: ви бачите логіку рішень і стан пошуку в будь-який момент.',
    steps: [
      'Бриф',
      'Договір',
      'Search Strategy',
      'Research & Hunting',
      'Shortlist',
      'Offer & Exit',
    ],
    timeValue: '≈ 14 днів',
    timeLabel: 'середній строк до shortlist',
    timeCaveat:
      'Фактичний строк залежить від позиції, географії, вимог і ситуації на ринку.',
  },
  shortlist: {
    title: 'Не потік резюме. Shortlist людей, з якими варто зустрічатися',
    lead: 'Результат пошуку — не кількість надісланих кандидатів, а кілька профілів, кожен з яких відповідає бізнес-задачі за вакансією.',
    demoNote: 'Демонстраційний приклад воронки',
    stages: [
      { value: '120+', label: 'досліджених спеціалістів' },
      { value: '30+', label: 'релевантних профілів' },
      { value: '12', label: 'прямих діалогів' },
      { value: '4', label: 'кандидати в shortlist' },
      { value: '1', label: 'accepted offer' },
    ],
    deliverables: [
      'Market Research',
      'Qualified Candidates',
      'Shortlist',
      'Market Feedback',
    ],
  },
  cases: {
    title: 'Складний пошук найкраще пояснюється результатом',
    lead: 'Три приклади того, як виглядає пошук, коли очевидний ринок уже пройдено.',
    placeholderNote:
      'Кейси нижче — демонстраційні заготовки. Перед запуском сайту вони будуть замінені реальними історіями.',
    roleLabel: 'Роль',
    industryLabel: 'Індустрія',
    geoLabel: 'Географія',
    challengeLabel: 'Challenge',
    approachLabel: 'Approach',
    resultLabel: 'Result',
    cta: 'Дивитися кейс',
    items: [
      {
        role: 'CFO',
        industry: 'International Business',
        geo: 'Ukraine + Europe',
        challenge:
          'Компанії потрібен був фінансовий директор із досвідом роботи одночасно в українській і європейській юрисдикціях. Внутрішній пошук тривав чотири місяці без результату.',
        approach:
          'Побудували карту ринку з 40 компаній схожого масштабу в двох регіонах, вийшли напряму на 18 профілів, з яких 12 не розглядали зміну роботи.',
        result: 'Shortlist із 4 кандидатів за 16 днів · offer прийнято',
      },
      {
        role: 'CTO',
        industry: 'Product IT',
        geo: 'Ukraine + Poland',
        challenge:
          'Продуктова компанія масштабувала команду розробки і потребувала технічного директора з досвідом побудови інженерної організації з нуля до 100+ людей.',
        approach:
          'Пошук вівся конфіденційно, без публічної вакансії. Дослідили ринок tech-лідерів у двох країнах, провели 9 прямих діалогів.',
        result: 'Shortlist із 3 кандидатів за 21 день · hire з релокацією',
      },
      {
        role: 'Commercial Director',
        industry: 'Manufacturing',
        geo: 'Ukraine',
        challenge:
          'Виробнича компанія виходила в новий сегмент і шукала комерційного директора з підтвердженим досвідом побудови B2B-каналу в цій ніші.',
        approach:
          'Сегментували ринок за типом каналу продажів, а не за назвою посади — це дало вихід на кандидатів із суміжних галузей.',
        result: 'Shortlist із 5 кандидатів за 12 днів · offer прийнято',
      },
    ],
  },
  founder: {
    title: '20+ років у пошуку людей, від рішень яких залежить бізнес',
    name: 'Сергій Денисюк',
    role: 'Founder & Managing Partner, ESCA',
    paragraphs: [
      'Executive Search потребує більшого, ніж доступ до бази кандидатів.',
      'Потрібно зрозуміти бізнес-задачу за вакансією, побачити ринок ширше за job description і вміти говорити на одному рівні і з власником компанії, і з керівником, якого хантять з успішної позиції.',
    ],
  },
  proof: {
    title: '20+ років шукаємо людей на позиції, де ціна помилки особливо висока',
    stats: [
      { value: '20+', label: 'років досвіду' },
      { value: '3 500+', label: 'закритих вакансій' },
      { value: '500+', label: 'компаній-клієнтів' },
      { value: '2 місяці', label: 'гарантії заміни' },
      { value: 'Worldwide', label: 'географія пошуку' },
    ],
    logosTitle: 'Компанії, для яких ми закривали позиції',
    quotesTitle: 'Що кажуть клієнти',
    quotes: [
      {
        quote:
          'Найбільш вражаюче в цій компанії — розуміння вакансій і підхід до пошуку кандидатів. Працювати з ESCA було справжнім задоволенням.',
        author: 'Founder, Code-Smith',
      },
      {
        quote:
          'ESCA — відповідальні партнери з сильним послужним списком. Ми постійно зростаємо і наймаємо нових розробників. ESCA допомагає нам уже півтора року.',
        author: 'PM, Andromo LTD',
      },
      {
        quote:
          'Ми зверталися до ESCA для найму HRD і CFO. Задоволені всім процесом співпраці.',
        author: 'Managing Partner, SBSB Fintech Lawyers',
      },
    ],
  },
  geography: {
    title: 'Шукаємо там, де знаходиться потрібний спеціаліст',
    lead: 'Один search-партнер замість нового агентства в кожній країні',
    regions: ['Ukraine', 'Europe', 'United Kingdom', 'USA', 'Worldwide'],
  },
  guarantee: {
    title: 'Відповідальність ESCA не закінчується в день прийнятого оферу',
    value: '2 місяці',
    valueLabel: 'гарантії заміни',
    text: 'Якщо виникає передбачений договором гарантійний випадок, ми повторно запускаємо пошук.',
  },
  price: {
    title: 'Вартість відома до початку пошуку',
    lead: 'Ціна розраховується з урахуванням:',
    factors: [
      'рівня позиції',
      'компенсації кандидата',
      'складності пошуку',
      'географії',
    ],
    note: 'Після узгодження вартість фіксується в договорі.',
    cta: 'Отримати оцінку вартості',
  },
  faq: {
    title: 'Часті питання',
    items: [
      {
        q: 'Скільки займає пошук?',
        a: 'У середньому близько 14 днів до shortlist. Фактичний строк залежить від рівня позиції, географії та ситуації на ринку — складні C-level пошуки можуть тривати 4–6 тижнів.',
      },
      {
        q: 'Коли зʼявляться перші кандидати?',
        a: 'Перші профілі з карти ринку ви отримуєте протягом 5–7 робочих днів після затвердження search strategy.',
      },
      {
        q: 'Як розраховується вартість?',
        a: 'Ціна залежить від рівня позиції, компенсації кандидата, складності пошуку та географії. Ми називаємо суму до старту, після брифу, і фіксуємо її в договорі.',
      },
      {
        q: 'ESCA працює тільки з C-level?',
        a: 'Ні. Крім C-level ми закриваємо B-level позиції та складні технічні ролі, де стандартного підбору недостатньо.',
      },
      {
        q: 'Працюєте за межами України?',
        a: 'Так. Ведемо пошук в Європі, Великій Британії, США та за запитом — в інших регіонах. Один партнер веде пошук у всіх країнах проєкту.',
      },
      {
        q: 'Чи можна вести пошук конфіденційно?',
        a: 'Так, це стандартна практика Executive Search. Ми підписуємо NDA і ведемо пошук без розкриття назви компанії до фінальних переговорів.',
      },
      {
        q: 'Що, якщо ми вже шукали самі?',
        a: 'Це часта ситуація. Ми починаємо з аналізу того, який сегмент ринку вже пройдено, і будуємо пошук за межами очевидного кола кандидатів.',
      },
      {
        q: 'Як працює гарантія заміни?',
        a: 'Якщо протягом двох місяців виникає передбачений договором гарантійний випадок, ми повторно запускаємо пошук на цю позицію. Умови фіксуються в договорі до старту.',
      },
    ],
  },
  contact: {
    title: 'Розкажіть, кого потрібно знайти',
    lead: 'Не обовʼязково готувати ідеальний job description. Опишіть задачу так, як бачите її зараз. Ми розберемо позицію, оцінимо ринок і запропонуємо підхід до пошуку.',
    nameLabel: "Імʼя",
    namePlaceholder: 'Як до вас звертатися',
    companyLabel: 'Компанія',
    companyPlaceholder: 'Назва компанії',
    contactLabel: 'Телефон або Email',
    contactPlaceholder: '+380 (0XX) XXX-XX-XX або name@company.com',
    roleLabel: 'Кого шукаєте?',
    rolePlaceholder: 'Наприклад, CFO для міжнародного бізнесу',
    submit: 'Обговорити пошук',
    submitting: 'Надсилаємо…',
    disclaimer:
      'Перша розмова ні до чого не зобовʼязує. Запит залишається конфіденційним.',
    successTitle: 'Отримали запит',
    successText:
      'Звʼяжемося з вами, щоб уточнити деталі позиції та зрозуміти, який формат пошуку підійде найкраще.',
    successReset: 'Надіслати ще один запит',
    errorGeneric: 'Не вдалося надіслати. Спробуйте ще раз або напишіть нам.',
    errors: {
      name: "Вкажіть імʼя",
      contact: 'Вкажіть телефон або email',
      contactInvalid: 'Перевірте телефон або email',
      role: 'Опишіть, кого шукаєте',
    },
  },
  footer: {
    tagline: 'Executive Search та пошук рідкісних спеціалістів. Worldwide.',
    navTitle: 'Розділи',
    contactTitle: 'Контакти',
    legacyTitle: 'Послуги',
    legacyLinks: [
      { label: 'Executive Search', to: '/services/executive-search' },
      { label: 'Підбір IT-спеціалістів', to: '/services/it-recruitment' },
      { label: 'Комплектація команди', to: '/services/team-building' },
      { label: "Кар'єрний консалтинг", to: '/services/career-consulting' },
    ],
    rights: 'Всі права захищені.',
  },
  stickyCta: 'Обговорити вакансію',
}

const EN: EscaCopy = {
  nav: {
    expertise: 'Executive Search Expertise',
    research: 'How We Search',
    cases: 'Cases',
    about: 'About ESCA',
    cta: 'Discuss a role',
    menu: 'Menu',
    close: 'Close',
  },
  hero: {
    eyebrow: 'Executive Search / Worldwide',
    h1: 'We find the leaders and rare specialists who are not on the open market',
    lead: 'Executive Search for C-level, B-level and hard-to-fill IT roles. We research the market, approach the right people directly and bring into conversation candidates who are not looking for a new job.',
    cta: 'Discuss a role',
    facts: [
      { value: '20+', label: 'years of experience' },
      { value: '3,500+', label: 'roles filled' },
      { value: '500+', label: 'client companies' },
      { value: 'Worldwide', label: 'search coverage' },
    ],
    visualCaption: 'Market map · interface demonstration',
  },
  scenarios: {
    title: 'When posting a vacancy is not enough, we go after the candidate directly',
    items: [
      {
        title: 'You need to strengthen leadership',
        text: 'A CEO, COO, CFO, CTO or function head whose decisions determine business results.',
      },
      {
        title: 'The search has stalled',
        text: 'Your in-house HR team has already covered the obvious market, but there are not enough strong candidates.',
      },
      {
        title: 'You need a confidential replacement',
        text: 'We find a new leader without a public vacancy.',
      },
      {
        title: 'You are entering a new market',
        text: 'We find your first leaders and key specialists in another country.',
      },
    ],
  },
  expertise: {
    title: 'We take on roles where standard recruiting is no longer enough',
    lead: 'We build a separate market map for every category — the roles below are what we work on most often.',
    categories: [
      {
        name: 'C-level & Executive',
        roles: [
          'CEO / Chief Executive Officer',
          'COO / Chief Operating Officer',
          'CFO / Chief Financial Officer',
          'CTO / Chief Technology Officer',
          'CMO / Chief Marketing Officer',
          'CHRO / HR Director',
          'Country Manager',
          'Board Member / Advisor',
        ],
      },
      {
        name: 'IT & Digital',
        roles: [
          'VP of Engineering',
          'Head of Engineering',
          'Tech Lead / Team Lead',
          'Solution / Cloud Architect',
          'Senior Backend / Frontend Developer',
          'DevOps / SRE Engineer',
          'Data Scientist / ML Engineer',
          'Product Manager',
        ],
      },
      {
        name: 'Finance & Banking',
        roles: [
          'Finance Director',
          'Head of Treasury',
          'Financial Controller',
          'Head of Internal Audit',
          'Risk Manager',
          'Head of Compliance',
          'Investment Manager',
          'Chief Accountant',
        ],
      },
      {
        name: 'Sales & Business Development',
        roles: [
          'Commercial Director',
          'Head of Sales',
          'Business Development Director',
          'Key Account Director',
          'Head of B2B Sales',
          'Regional Sales Manager',
          'Partnerships Director',
        ],
      },
      {
        name: 'Marketing & PR',
        roles: [
          'Marketing Director',
          'Head of Brand',
          'Head of Performance Marketing',
          'Head of Communications',
          'PR Director',
          'Head of Product Marketing',
          'Creative Director',
        ],
      },
      {
        name: 'HR & People',
        roles: [
          'HR Director',
          'Head of Talent Acquisition',
          'C&B Manager',
          'Head of L&D',
          'HR Business Partner',
          'People Operations Lead',
        ],
      },
      {
        name: 'Logistics & Operations',
        roles: [
          'Operations Director',
          'Supply Chain Director',
          'Head of Logistics',
          'Plant / Production Director',
          'Procurement Director',
          'Head of Warehouse Operations',
        ],
      },
      {
        name: 'Legal',
        roles: [
          'Legal Director',
          'General Counsel',
          'Head of Corporate Law',
          'Compliance Officer',
          'M&A Lawyer',
          'Contract Manager',
        ],
      },
      {
        name: 'E-commerce & Retail',
        roles: [
          'E-commerce Director',
          'Head of Retail',
          'Category Director',
          'Head of Marketplace',
          'Retail Operations Manager',
          'Head of Customer Experience',
        ],
      },
    ],
    footTitle: 'Did not find the role you need?',
    footText:
      'We research the market separately for every position and determine where specialists with the required experience actually are.',
    footCta: 'Discuss the role',
  },
  research: {
    eyebrow: 'How We Search',
    title: 'We do not wait for applications. We find the people your business needs',
    lead: 'Every search starts with market research, not with a job posting.',
    funnel: [
      { value: 'MARKET', label: 'market map' },
      { value: '156', label: 'profiles' },
      { value: '37', label: 'matches' },
      { value: '11', label: 'contacted' },
      { value: '4', label: 'shortlisted' },
    ],
    steps: [
      {
        title: 'Market Mapping',
        text: 'We define the companies, industries and markets where the right specialist is likely to be.',
      },
      {
        title: 'Research',
        text: 'We identify potential candidates and build profiles far beyond job boards.',
      },
      {
        title: 'Direct Hunting',
        text: 'We approach people directly, including those who are not actively looking for a job.',
      },
      {
        title: 'Qualification',
        text: 'We verify experience, motivation and expectations regarding the role and compensation.',
      },
      {
        title: 'Shortlist',
        text: 'We hand over the selected candidates together with feedback from the market.',
      },
    ],
  },
  process: {
    title: 'You know exactly what is happening with the search at every stage',
    lead: 'No step stays a black box: you see the reasoning behind every decision and the state of the search at any moment.',
    steps: [
      'Brief',
      'Agreement',
      'Search Strategy',
      'Research & Hunting',
      'Shortlist',
      'Offer & Exit',
    ],
    timeValue: '≈ 14 days',
    timeLabel: 'average time to shortlist',
    timeCaveat:
      'The actual timeline depends on the role, geography, requirements and market conditions.',
  },
  shortlist: {
    title: 'Not a stream of CVs. A shortlist of people worth meeting',
    lead: 'The result of a search is not how many candidates were sent, but a handful of profiles that each answer the business problem behind the role.',
    demoNote: 'Illustrative funnel example',
    stages: [
      { value: '120+', label: 'specialists researched' },
      { value: '30+', label: 'relevant profiles' },
      { value: '12', label: 'direct conversations' },
      { value: '4', label: 'candidates shortlisted' },
      { value: '1', label: 'accepted offer' },
    ],
    deliverables: [
      'Market Research',
      'Qualified Candidates',
      'Shortlist',
      'Market Feedback',
    ],
  },
  cases: {
    title: 'A difficult search is best explained by its result',
    lead: 'Three examples of what a search looks like once the obvious market has already been covered.',
    placeholderNote:
      'The cases below are placeholders. They will be replaced with real stories before launch.',
    roleLabel: 'Role',
    industryLabel: 'Industry',
    geoLabel: 'Geography',
    challengeLabel: 'Challenge',
    approachLabel: 'Approach',
    resultLabel: 'Result',
    cta: 'View case',
    items: [
      {
        role: 'CFO',
        industry: 'International Business',
        geo: 'Ukraine + Europe',
        challenge:
          'The company needed a finance director with hands-on experience across both Ukrainian and European jurisdictions. The in-house search had run for four months without result.',
        approach:
          'We mapped 40 comparable companies across two regions and approached 18 profiles directly, 12 of whom were not considering a move.',
        result: 'Shortlist of 4 in 16 days · offer accepted',
      },
      {
        role: 'CTO',
        industry: 'Product IT',
        geo: 'Ukraine + Poland',
        challenge:
          'A product company was scaling engineering and needed a CTO who had built an engineering organisation from zero to 100+ people.',
        approach:
          'The search ran confidentially, with no public vacancy. We mapped tech leaders across two countries and held 9 direct conversations.',
        result: 'Shortlist of 3 in 21 days · hire with relocation',
      },
      {
        role: 'Commercial Director',
        industry: 'Manufacturing',
        geo: 'Ukraine',
        challenge:
          'A manufacturer entering a new segment needed a commercial director with a proven record of building a B2B channel in that niche.',
        approach:
          'We segmented the market by sales-channel type rather than job title, which opened access to candidates from adjacent industries.',
        result: 'Shortlist of 5 in 12 days · offer accepted',
      },
    ],
  },
  founder: {
    title: '20+ years finding the people whose decisions shape the business',
    name: 'Sergey Denisyuk',
    role: 'Founder & Managing Partner, ESCA',
    paragraphs: [
      'Executive Search takes more than access to a candidate database.',
      'You have to understand the business problem behind the role, see the market beyond the job description, and be able to hold a conversation as an equal both with the company owner and with the executive you are approaching in a successful position.',
    ],
  },
  proof: {
    title: 'For 20+ years we have been filling roles where the cost of a mistake is highest',
    stats: [
      { value: '20+', label: 'years of experience' },
      { value: '3,500+', label: 'roles filled' },
      { value: '500+', label: 'client companies' },
      { value: '2 months', label: 'replacement guarantee' },
      { value: 'Worldwide', label: 'search coverage' },
    ],
    logosTitle: 'Companies we have hired for',
    quotesTitle: 'What clients say',
    quotes: [
      {
        quote:
          'The most impressive thing about this company is their understanding of vacancies and their approach to finding candidates. Working with ESCA was a genuine pleasure.',
        author: 'Founder, Code-Smith',
      },
      {
        quote:
          'ESCA are highly responsible partners with a strong track record. We are constantly growing and hiring new developers. ESCA has been helping us for 1.5 years now.',
        author: 'PM, Andromo LTD',
      },
      {
        quote:
          'We used ESCA to hire our HRD and CFO. We are satisfied with the entire cooperation process.',
        author: 'Managing Partner, SBSB Fintech Lawyers',
      },
    ],
  },
  geography: {
    title: 'We search where the right specialist actually is',
    lead: 'One search partner instead of a new agency in every country',
    regions: ['Ukraine', 'Europe', 'United Kingdom', 'USA', 'Worldwide'],
  },
  guarantee: {
    title: "ESCA's responsibility does not end on the day the offer is accepted",
    value: '2 months',
    valueLabel: 'replacement guarantee',
    text: 'If a guarantee case covered by the agreement occurs, we run the search again.',
  },
  price: {
    title: 'The cost is known before the search begins',
    lead: 'The price is calculated based on:',
    factors: [
      'seniority of the role',
      'candidate compensation',
      'complexity of the search',
      'geography',
    ],
    note: 'Once agreed, the fee is fixed in the contract.',
    cta: 'Get a cost estimate',
  },
  faq: {
    title: 'Frequently asked questions',
    items: [
      {
        q: 'How long does a search take?',
        a: 'On average around 14 days to shortlist. The actual timeline depends on seniority, geography and market conditions — complex C-level searches can take 4–6 weeks.',
      },
      {
        q: 'When will the first candidates appear?',
        a: 'You receive the first profiles from the market map within 5–7 working days of approving the search strategy.',
      },
      {
        q: 'How is the fee calculated?',
        a: 'The price depends on seniority, candidate compensation, complexity and geography. We quote before the start, after the briefing, and fix the figure in the contract.',
      },
      {
        q: 'Does ESCA only work with C-level?',
        a: 'No. Alongside C-level we fill B-level roles and complex technical positions where standard recruiting is not enough.',
      },
      {
        q: 'Do you work outside Ukraine?',
        a: 'Yes. We run searches across Europe, the UK, the USA and, on request, other regions. One partner handles the search in every country of the project.',
      },
      {
        q: 'Can the search be run confidentially?',
        a: 'Yes, this is standard practice in Executive Search. We sign an NDA and run the search without disclosing the company name until final negotiations.',
      },
      {
        q: 'What if we have already searched ourselves?',
        a: 'This is a common situation. We start by analysing which market segment has already been covered and build the search beyond the obvious candidate pool.',
      },
      {
        q: 'How does the replacement guarantee work?',
        a: 'If a guarantee case covered by the agreement occurs within two months, we run the search for that role again. The terms are fixed in the contract before we start.',
      },
    ],
  },
  contact: {
    title: 'Tell us who you need to find',
    lead: 'You do not need a perfect job description. Describe the problem as you see it right now. We will unpack the role, assess the market and propose an approach to the search.',
    nameLabel: 'Name',
    namePlaceholder: 'How should we address you',
    companyLabel: 'Company',
    companyPlaceholder: 'Company name',
    contactLabel: 'Phone or Email',
    contactPlaceholder: '+380 (0XX) XXX-XX-XX or name@company.com',
    roleLabel: 'Who are you looking for?',
    rolePlaceholder: 'For example, a CFO for an international business',
    submit: 'Discuss the search',
    submitting: 'Sending…',
    disclaimer:
      'The first conversation commits you to nothing. Your request stays confidential.',
    successTitle: 'Request received',
    successText:
      'We will get in touch to clarify the details of the role and work out which search format fits best.',
    successReset: 'Send another request',
    errorGeneric: 'Could not send. Please try again or write to us directly.',
    errors: {
      name: 'Please enter your name',
      contact: 'Please enter a phone number or email',
      contactInvalid: 'Please check the phone number or email',
      role: 'Please describe who you are looking for',
    },
  },
  footer: {
    tagline: 'Executive Search and rare-specialist hiring. Worldwide.',
    navTitle: 'Sections',
    contactTitle: 'Contacts',
    legacyTitle: 'Services',
    legacyLinks: [
      { label: 'Executive Search', to: '/services/executive-search' },
      { label: 'IT Recruitment', to: '/services/it-recruitment' },
      { label: 'Team Building', to: '/services/team-building' },
      { label: 'Career Consulting', to: '/services/career-consulting' },
    ],
    rights: 'All rights reserved.',
  },
  stickyCta: 'Discuss a role',
}

const COPY: Record<EscaLang, EscaCopy> = { UA, EN }

export function useEscaCopy(lang: Lang): EscaCopy {
  return COPY[toEscaLang(lang)]
}
